import { Box, Button } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import {
  ResetPasswordModal,
  useRefocusAfterSave,
  useResetPasswordAction,
  useWriteAnnouncer
} from "../../../components/UserInfo";

export interface HeaderActionsProps {
  /**
   * Whether the User Information form differs from the stored record. `MyInfo` derives it once for
   * both this and `usePendingEditsGuard`, so Save and the prompt never disagree. Defaults to `false`,
   * so a caller that forgets it gets an inactive Save.
   */
  isDirty?: boolean;
  isSaving?: boolean;
  /** Resolves `true` once saved; `false` when nothing was sent, validation blocked it, or it failed. */
  onSave?: () => Promise<boolean>;
  userId?: string;
  accountId?: string;
  /** Selects the reset failure copy's register. Defaults to `false`, the impersonal one. */
  isSubjectTheOperator?: boolean;
}

/**
 * FR-008's single page-header action slot: one Save, plus Reset Password. Both are live
 * (`specs/my-info-write`); the save and reset logic is shared with User Manager's `HeaderActions`.
 *
 * **This component announces and toasts both writes**; the page never announces. Closing the reset
 * dialog unmounts it, discarding what was typed. On success this closes it and announces; on failure
 * it stays open with `errorMessage` (see `useResetPasswordAction` for an outcome that lands after
 * the dialog was closed).
 *
 * **Bridge users get no up-front gate** (FR-018 superseded, CC6 parity). A linked user's reset is
 * refused upstream, and the 403 shows in the dialog (`PW_BRIDGE` on the operator's own record,
 * `UM_PW_ERR_LINKED` while acting as someone).
 */
export const HeaderActions = ({
  isDirty = false,
  isSaving = false,
  onSave,
  userId,
  accountId,
  isSubjectTheOperator = false
}: HeaderActionsProps) => {
  const announcer = useWriteAnnouncer();
  const {
    isResetOpen,
    resetError,
    isResetting,
    handleResetOpen,
    handleResetClose,
    handleResetConfirm
  } = useResetPasswordAction({ userId, accountId, isSubjectTheOperator });
  const refocusAfterSave = useRefocusAfterSave({
    isSaving,
    saveTestId: "myinfo-save",
    fallbackTestId: "myinfo-reset-password"
  });

  const handleSave = async () => {
    if (!onSave) {
      return;
    }

    announcer.clear();
    if (await onSave()) {
      announcer.success("UM_SAVE_SUCCESS");
    }
    refocusAfterSave();
  };

  return (
    <Box
      data-testid={"myinfo-header-actions-content"}
      sx={{
        alignItems: "center",
        columnGap: "8px",
        display: "flex",
        flexWrap: "wrap",
        rowGap: "8px"
      }}
    >
      {/*
       * Reset Password first, Save last — the approved prototype's order, and the conventional one:
       * the primary action sits at the end of the cluster, nearest the edge the eye finishes on. The
       * prototype's own header calls this order non-authoritative, so this is fidelity to the reviewed
       * mock rather than a requirement; it is safe to reorder if the team prefers otherwise.
       */}
      <Button
        analyticsConfiguration={{
          eventName: "myinfo-reset-password",
          eventProperties: undefined
        }}
        buttonStyle={"tertiary"}
        data-testid={"myinfo-reset-password"}
        onClick={handleResetOpen}
      >
        {Locale.getText("RESET_PASSWORD") as string}
      </Button>

      <Button
        analyticsConfiguration={{
          eventName: "myinfo-save",
          eventProperties: undefined
        }}
        buttonStyle={"primary"}
        data-testid={"myinfo-save"}
        disabled={!isDirty || isSaving}
        onClick={handleSave}
      >
        {Locale.getText("SAVE") as string}
      </Button>

      <ResetPasswordModal
        errorMessage={resetError}
        isOpen={isResetOpen}
        isSaving={isResetting}
        onClose={handleResetClose}
        onConfirm={handleResetConfirm}
      />
    </Box>
  );
};
